import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, Smartphone, Key, AlertCircle, Check, X, 
  Loader2, RefreshCw, Copy, CheckCircle, 
  LogOut, Monitor, Clock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SecuritySettings Component
 * 
 * Provides UI for:
 * - 2FA setup, verification, and management
 * - Active sessions view and revocation
 * - Security status overview
 */
export function SecuritySettings() {
  const { token, logout } = useAuth();
  
  // Security status state
  const [securityStatus, setSecurityStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // 2FA setup state
  const [setupData, setSetupData] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [backupCodesShown, setBackupCodesShown] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  
  // 2FA disable state
  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState('');
  
  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokeLoading, setRevokeLoading] = useState(null);

  const fetchSecurityStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/security/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSecurityStatus(data);
      }
    } catch (err) {
      setError('Failed to load security status');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const response = await fetch(`${API_URL}/api/auth/security/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, [token]);

  // Fetch security status
  useEffect(() => {
    fetchSecurityStatus();
    fetchSessions();
  }, [fetchSecurityStatus, fetchSessions]);

  // 2FA Setup Flow
  const startSetup = async () => {
    try {
      setVerifyError('');
      const response = await fetch(`${API_URL}/api/auth/security/2fa/setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSetupData(data);
        setShowSetup(true);
      } else {
        const err = await response.json();
        setVerifyError(err.detail || 'Failed to start 2FA setup');
      }
    } catch (err) {
      setVerifyError('Failed to start 2FA setup');
    }
  };

  const verifyAndEnable = async () => {
    if (!verifyCode || verifyCode.length < 6) {
      setVerifyError('Please enter a 6-digit code');
      return;
    }
    
    try {
      setVerifyLoading(true);
      setVerifyError('');
      
      const response = await fetch(`${API_URL}/api/auth/security/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: verifyCode,
          backup_codes: setupData.backup_codes
        })
      });
      
      if (response.ok) {
        // Show backup codes confirmation
        setBackupCodesShown(true);
      } else {
        const err = await response.json();
        setVerifyError(err.detail || 'Invalid code. Please try again.');
      }
    } catch (err) {
      setVerifyError('Verification failed. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const completeSetup = () => {
    setShowSetup(false);
    setSetupData(null);
    setVerifyCode('');
    setBackupCodesShown(false);
    setCopiedCodes(false);
    fetchSecurityStatus();
  };

  const copyBackupCodes = () => {
    if (setupData?.backup_codes) {
      const codesText = setupData.backup_codes.join('\n');
      navigator.clipboard.writeText(codesText);
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 3000);
    }
  };

  // 2FA Disable Flow
  const disable2FA = async () => {
    if (!disableCode) {
      setDisableError('Please enter your 2FA code');
      return;
    }
    
    try {
      setDisableLoading(true);
      setDisableError('');
      
      const response = await fetch(`${API_URL}/api/auth/security/2fa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: disableCode })
      });
      
      if (response.ok) {
        setShowDisable(false);
        setDisableCode('');
        fetchSecurityStatus();
        // User will be logged out due to session revocation
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        const err = await response.json();
        setDisableError(err.detail || 'Invalid code');
      }
    } catch (err) {
      setDisableError('Failed to disable 2FA');
    } finally {
      setDisableLoading(false);
    }
  };

  // Session Management
  const revokeSession = async (sessionId) => {
    try {
      setRevokeLoading(sessionId);
      const response = await fetch(`${API_URL}/api/auth/security/sessions/${sessionId}/revoke`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSessions();
      }
    } catch (err) {
      console.error('Failed to revoke session:', err);
    } finally {
      setRevokeLoading(null);
    }
  };

  const revokeAllSessions = async () => {
    try {
      setRevokeLoading('all');
      const response = await fetch(`${API_URL}/api/auth/security/sessions/revoke-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchSessions();
        // Current session is revoked, logout
        setTimeout(() => {
          logout();
        }, 1000);
      }
    } catch (err) {
      console.error('Failed to revoke all sessions:', err);
    } finally {
      setRevokeLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (userAgent) => {
    if (userAgent?.toLowerCase().includes('mobile')) {
      return <Smartphone size={16} />;
    }
    return <Monitor size={16} />;
  };

  if (loading) {
    return (
      <div className="glass-panel" data-testid="security-loading">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <Loader2 size={24} className="animate-spin" style={{ marginRight: '0.5rem' }} />
          Loading security settings...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="security-settings">
      {/* Security Overview */}
      <div className="glass-panel" data-testid="security-overview">
        <div className="glass-panel-header">
          <div className="glass-panel-title">
            <Shield size={18} className="icon" />
            Security Overview
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', padding: '1rem' }}>
          <div style={{ 
            background: 'var(--glass-bg)', 
            borderRadius: '8px', 
            padding: '1rem',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold',
              color: securityStatus?.two_factor_enabled ? 'var(--status-success)' : 'var(--status-warning)'
            }}>
              {securityStatus?.two_factor_enabled ? <Check size={24} /> : <X size={24} />}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              2FA {securityStatus?.two_factor_enabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
          
          <div style={{ 
            background: 'var(--glass-bg)', 
            borderRadius: '8px', 
            padding: '1rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {securityStatus?.active_sessions || 0}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Active Sessions
            </div>
          </div>
          
          {securityStatus?.two_factor_enabled && (
            <div style={{ 
              background: 'var(--glass-bg)', 
              borderRadius: '8px', 
              padding: '1rem',
              textAlign: 'center'
            }}>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: 'bold',
                color: securityStatus?.backup_codes_remaining > 3 ? 'var(--status-success)' : 'var(--status-warning)'
              }}>
                {securityStatus?.backup_codes_remaining || 0}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                Backup Codes Left
              </div>
            </div>
          )}
        </div>
        
        {securityStatus?.two_factor_required && !securityStatus?.two_factor_enabled && (
          <div style={{ 
            background: 'rgba(251, 191, 36, 0.1)', 
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            margin: '0 1rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }} data-testid="2fa-required-notice">
            <AlertCircle size={18} style={{ color: 'var(--status-warning)' }} />
            <span style={{ color: 'var(--status-warning)', fontSize: '0.875rem' }}>
              Two-factor authentication is required for your role. Please enable it.
            </span>
          </div>
        )}
      </div>

      {/* Two-Factor Authentication */}
      <div className="glass-panel" data-testid="2fa-section">
        <div className="glass-panel-header">
          <div className="glass-panel-title">
            <Key size={18} className="icon" />
            Two-Factor Authentication
          </div>
        </div>
        
        <div className="settings-option" style={{ padding: '1rem' }}>
          <div>
            <div className="settings-option-label">Authenticator App</div>
            <div className="settings-option-desc">
              Use an authenticator app (Google Authenticator, Authy) to generate codes
            </div>
          </div>
          
          {securityStatus?.two_factor_enabled ? (
            <button 
              className="btn btn-danger" 
              onClick={() => setShowDisable(true)}
              data-testid="disable-2fa-btn"
            >
              Disable 2FA
            </button>
          ) : (
            <button 
              className="btn btn-primary" 
              onClick={startSetup}
              data-testid="enable-2fa-btn"
            >
              Enable 2FA
            </button>
          )}
        </div>
        
        {verifyError && !showSetup && (
          <div style={{ 
            color: 'var(--status-danger)', 
            fontSize: '0.875rem',
            padding: '0 1rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={16} />
            {verifyError}
          </div>
        )}
      </div>

      {/* 2FA Setup Modal */}
      {showSetup && setupData && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} data-testid="2fa-setup-modal">
          <div className="glass-panel" style={{ 
            width: '100%', 
            maxWidth: '450px', 
            maxHeight: '90vh',
            overflow: 'auto',
            margin: '1rem'
          }}>
            <div className="glass-panel-header">
              <div className="glass-panel-title">
                <Shield size={18} className="icon" />
                {backupCodesShown ? 'Save Backup Codes' : 'Set Up Two-Factor Authentication'}
              </div>
              {!backupCodesShown && (
                <button 
                  onClick={() => { setShowSetup(false); setSetupData(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                >
                  <X size={20} />
                </button>
              )}
            </div>
            
            {!backupCodesShown ? (
              <div style={{ padding: '1.5rem' }}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.875rem' }}>
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  
                  <div style={{ 
                    background: 'white', 
                    borderRadius: '8px', 
                    padding: '1rem',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <img 
                      src={`data:image/png;base64,${setupData.qr_code}`} 
                      alt="2FA QR Code"
                      style={{ width: '200px', height: '200px' }}
                      data-testid="2fa-qr-code"
                    />
                  </div>
                </div>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.75rem', 
                    color: 'var(--text-secondary)',
                    marginBottom: '0.5rem'
                  }}>
                    Or enter this code manually:
                  </label>
                  <div style={{
                    fontFamily: 'monospace',
                    background: 'var(--glass-bg)',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    wordBreak: 'break-all',
                    fontSize: '0.875rem'
                  }} data-testid="2fa-secret">
                    {setupData.secret}
                  </div>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.875rem', 
                    marginBottom: '0.5rem' 
                  }}>
                    Enter verification code from app:
                  </label>
                  <input
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      background: 'var(--glass-bg)',
                      color: 'var(--text-primary)',
                      fontSize: '1.25rem',
                      textAlign: 'center',
                      letterSpacing: '0.5rem'
                    }}
                    maxLength={6}
                    data-testid="2fa-verify-input"
                  />
                </div>
                
                {verifyError && (
                  <div style={{ 
                    color: 'var(--status-danger)', 
                    fontSize: '0.875rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <AlertCircle size={16} />
                    {verifyError}
                  </div>
                )}
                
                <button 
                  className="btn btn-primary"
                  onClick={verifyAndEnable}
                  disabled={verifyLoading || verifyCode.length < 6}
                  style={{ width: '100%' }}
                  data-testid="2fa-verify-btn"
                >
                  {verifyLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" style={{ marginRight: '0.5rem' }} />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Enable'
                  )}
                </button>
              </div>
            ) : (
              <div style={{ padding: '1.5rem' }}>
                <div style={{ 
                  background: 'rgba(34, 197, 94, 0.1)', 
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <CheckCircle size={24} style={{ color: 'var(--status-success)' }} />
                  <div>
                    <div style={{ fontWeight: '600', color: 'var(--status-success)' }}>
                      2FA Enabled Successfully
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      Save your backup codes before closing
                    </div>
                  </div>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '0.875rem', 
                    marginBottom: '0.5rem',
                    fontWeight: '600'
                  }}>
                    Backup Codes (save these somewhere safe):
                  </label>
                  <div style={{
                    background: 'var(--glass-bg)',
                    borderRadius: '8px',
                    padding: '1rem',
                    fontFamily: 'monospace'
                  }} data-testid="backup-codes-list">
                    {setupData.backup_codes.map((code, i) => (
                      <div key={i} style={{ padding: '0.25rem 0' }}>
                        {code}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button 
                    className="btn btn-secondary"
                    onClick={copyBackupCodes}
                    style={{ flex: 1 }}
                    data-testid="copy-codes-btn"
                  >
                    {copiedCodes ? (
                      <>
                        <Check size={16} style={{ marginRight: '0.5rem' }} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={16} style={{ marginRight: '0.5rem' }} />
                        Copy Codes
                      </>
                    )}
                  </button>
                </div>
                
                <div style={{ 
                  background: 'rgba(251, 191, 36, 0.1)', 
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  marginBottom: '1.5rem',
                  fontSize: '0.75rem',
                  color: 'var(--status-warning)'
                }}>
                  <AlertCircle size={14} style={{ marginRight: '0.5rem', display: 'inline' }} />
                  Each backup code can only be used once. Keep them secure.
                </div>
                
                <button 
                  className="btn btn-primary"
                  onClick={completeSetup}
                  style={{ width: '100%' }}
                  data-testid="complete-setup-btn"
                >
                  I&apos;ve Saved My Codes
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 2FA Disable Modal */}
      {showDisable && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} data-testid="2fa-disable-modal">
          <div className="glass-panel" style={{ width: '100%', maxWidth: '400px', margin: '1rem' }}>
            <div className="glass-panel-header">
              <div className="glass-panel-title">
                <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
                Disable Two-Factor Authentication
              </div>
              <button 
                onClick={() => { setShowDisable(false); setDisableCode(''); setDisableError(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.875rem' }}>
                Enter your 2FA code to disable two-factor authentication. You will be logged out after disabling.
              </p>
              
              <div style={{ marginBottom: '1rem' }}>
                <input
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    background: 'var(--glass-bg)',
                    color: 'var(--text-primary)',
                    fontSize: '1.25rem',
                    textAlign: 'center',
                    letterSpacing: '0.5rem'
                  }}
                  maxLength={6}
                  data-testid="2fa-disable-input"
                />
              </div>
              
              {disableError && (
                <div style={{ 
                  color: 'var(--status-danger)', 
                  fontSize: '0.875rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AlertCircle size={16} />
                  {disableError}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => { setShowDisable(false); setDisableCode(''); setDisableError(''); }}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={disable2FA}
                  disabled={disableLoading || disableCode.length < 6}
                  style={{ flex: 1 }}
                  data-testid="2fa-disable-confirm-btn"
                >
                  {disableLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" style={{ marginRight: '0.5rem' }} />
                      Disabling...
                    </>
                  ) : (
                    'Disable 2FA'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Sessions */}
      <div className="glass-panel" data-testid="sessions-section">
        <div className="glass-panel-header">
          <div className="glass-panel-title">
            <Monitor size={18} className="icon" />
            Active Sessions
          </div>
          <button 
            className="btn btn-secondary btn-sm"
            onClick={fetchSessions}
            disabled={sessionsLoading}
          >
            <RefreshCw size={14} className={sessionsLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        
        <div style={{ padding: '1rem' }}>
          {sessions.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              color: 'var(--text-secondary)',
              padding: '2rem'
            }}>
              No active sessions
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1rem' }}>
                {sessions.map((session) => (
                  <div 
                    key={session.session_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      background: session.is_current ? 'rgba(99, 102, 241, 0.1)' : 'var(--glass-bg)',
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                      border: session.is_current ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid transparent'
                    }}
                    data-testid={`session-${session.session_id}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{ 
                        width: '36px', 
                        height: '36px', 
                        borderRadius: '8px',
                        background: 'var(--glass-bg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {getDeviceIcon(session.user_agent)}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                          {session.user_agent?.split(' ')[0] || 'Unknown Device'}
                          {session.is_current && (
                            <span style={{ 
                              marginLeft: '0.5rem',
                              fontSize: '0.75rem',
                              color: 'var(--accent-primary)',
                              fontWeight: 'normal'
                            }}>
                              (Current)
                            </span>
                          )}
                        </div>
                        <div style={{ 
                          fontSize: '0.75rem', 
                          color: 'var(--text-secondary)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <Clock size={12} />
                          Last active: {formatDate(session.last_active)}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => revokeSession(session.session_id)}
                      disabled={revokeLoading === session.session_id}
                      className="btn btn-secondary btn-sm"
                      style={{ color: 'var(--status-danger)' }}
                      data-testid={`revoke-session-${session.session_id}`}
                    >
                      {revokeLoading === session.session_id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <LogOut size={14} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
              
              {sessions.length > 1 && (
                <button
                  onClick={revokeAllSessions}
                  disabled={revokeLoading === 'all'}
                  className="btn btn-danger"
                  style={{ width: '100%' }}
                  data-testid="revoke-all-sessions-btn"
                >
                  {revokeLoading === 'all' ? (
                    <>
                      <Loader2 size={16} className="animate-spin" style={{ marginRight: '0.5rem' }} />
                      Revoking...
                    </>
                  ) : (
                    <>
                      <LogOut size={16} style={{ marginRight: '0.5rem' }} />
                      Sign Out All Devices
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SecuritySettings;
